'use client';
import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ── Toast System ──
interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

interface ToastContextType {
  showToast: (message: string, type?: Toast['type']) => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {} });
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: Toast['type'] = 'success') => {
    const id = Math.random().toString(36).slice(2);
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  const styles: Record<Toast['type'], string> = {
    success: 'bg-[#C3FB15]/10 border-[#C3FB15]/30 text-[#C3FB15]',
    error: 'bg-red-500/15 border-red-500/40 text-red-300',
    warning: 'bg-amber-500/15 border-amber-500/40 text-amber-300',
    info: 'bg-white/10 border-white/20 text-white',
  };
  const icons: Record<Toast['type'], string> = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <div
            key={t.id}
            className={`flex items-center gap-2.5 px-4 py-3 rounded-brand-md border text-sm font-medium shadow-xl backdrop-blur-md pointer-events-auto animate-in slide-in-from-right-5 duration-300 ${styles[t.type]}`}
          >
            <span className="text-base">{icons[t.type]}</span>
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ── Confirm Modal ──
interface ConfirmModalProps {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  loading?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}
export function ConfirmModal({ title, message, confirmLabel = 'Confirm', danger = false, loading = false, confirmDisabled = false, onConfirm, onCancel }: ConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-[#111111] border border-white/[0.02] rounded-brand-lg w-full max-w-sm">
        <div className="px-6 py-5">
          <div className="text-base font-semibold mb-2">{title}</div>
          <div className="text-sm text-white/60">{message}</div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/[0.02]">
          <Btn variant="ghost" onClick={onCancel} disabled={loading}>Cancel</Btn>
          <Btn variant={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={loading || confirmDisabled}>
            {loading ? '…' : confirmLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── Status Badge ──
const STATUS_STYLES: Record<string, string> = {
  published: 'bg-[#C3FB15]/10 text-[#C3FB15] border-[#C3FB15]/30',
  active: 'bg-[#C3FB15]/10 text-[#C3FB15] border-[#C3FB15]/30',
  ready: 'bg-[#C3FB15]/10 text-[#C3FB15] border-[#C3FB15]/30',
  success: 'bg-[#C3FB15]/10 text-[#C3FB15] border-[#C3FB15]/30',
  approved: 'bg-[#C3FB15]/10 text-[#C3FB15] border-[#C3FB15]/30',
  selected: 'bg-[#C3FB15]/10 text-[#C3FB15] border-[#C3FB15]/30',
  sent: 'bg-[#C3FB15]/10 text-[#C3FB15] border-[#C3FB15]/30',
  free: 'bg-[#C3FB15]/10 text-[#C3FB15] border-[#C3FB15]/30',
  draft: 'bg-white/8 text-white/50 border-white/15',
  unpublished: 'bg-white/8 text-white/50 border-white/15',
  inactive: 'bg-white/8 text-white/50 border-white/15',
  paused: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  review: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'under review': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'pending approval': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  scheduled: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  processing: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  shortlisted: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  failed: 'bg-red-500/15 text-red-400 border-red-500/30',
  expired: 'bg-red-500/15 text-red-400 border-red-500/30',
  rejected: 'bg-red-500/15 text-red-400 border-red-500/30',
  locked: 'bg-red-500/15 text-red-400 border-red-500/30',
  transcoding: 'bg-white/8 text-white/60 border-white/15',
  excellent: 'bg-[#C3FB15]/10 text-[#C3FB15] border-[#C3FB15]/30',
  'needs review': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  poor: 'bg-red-500/15 text-red-400 border-red-500/30',
  'not run': 'bg-white/8 text-white/50 border-white/15',
};

export function StatusBadge({ status }: { status: string }) {
  const key = status.toLowerCase().replace(/\s+/g, ' ').trim();
  const style = STATUS_STYLES[key] || STATUS_STYLES[key.replace(/\s+/g, '')] || 'bg-white/8 text-white/50 border-white/15';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${style}`}>
      {status}
    </span>
  );
}

// ── Metric Card ──
interface MetricCardProps {
  label: string;
  value: string;
  change?: string;
  positive?: boolean;
  icon?: string;
  sub?: string;
}
export function MetricCard({ label, value, change, positive, icon, sub }: MetricCardProps) {
  return (
    <div className="bg-[#111111] border border-white/[0.02] rounded-brand-lg p-4 hover:bg-white/[0.03] transition-all duration-150 hover:-translate-y-px">
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs text-white/50 font-medium">{label}</span>
        {icon && <span className="text-lg opacity-50">{icon}</span>}
      </div>
      <div className="text-2xl font-semibold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-white/40 mt-0.5">{sub}</div>}
      {change && (
        <div className={`text-xs mt-2 font-medium ${positive ? 'text-[#C3FB15]' : 'text-red-400'}`}>
          {positive ? '↑' : '↓'} {change}
        </div>
      )}
    </div>
  );
}

// ── Page Header ──
interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  breadcrumb?: string[];
}
export function PageHeader({ title, subtitle, actions, breadcrumb }: PageHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
      <div>
        {breadcrumb && (
          <div className="flex items-center gap-1.5 text-xs text-white/40 mb-1">
            {breadcrumb.map((b, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span>/</span>}
                <span>{b}</span>
              </React.Fragment>
            ))}
          </div>
        )}
        <h1 className="text-xl font-semibold tracking-tight font-display">{title}</h1>
        {subtitle && <p className="text-sm text-white/50 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ── Table ──
interface TableProps {
  columns: { key: string; label: React.ReactNode; width?: string }[];
  rows: Record<string, React.ReactNode>[];
  empty?: string;
}
export function Table({ columns, rows, empty }: TableProps) {
  return (
    <div className="overflow-x-auto rounded-brand-lg bg-white/[0.02] ring-1 ring-inset ring-white/[0.02]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.02] bg-white/[0.02]">
            {columns.map((col) => (
              <th key={col.key} className={`text-left px-4 py-3 text-xs font-semibold text-white/40 uppercase tracking-wider ${col.width || ''}`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-12 text-white/30 text-sm">
                {empty || 'No data found'}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={i} className="hover:bg-white/[0.03] transition-colors">
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3 text-white/80">
                    {row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Drag Handle (grip icon) ──
interface DragHandleProps {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  disabled?: boolean;
}
function DragHandle({ attributes, listeners, disabled }: DragHandleProps) {
  return (
    <button
      type="button"
      {...(disabled ? {} : attributes)}
      {...(disabled ? {} : listeners)}
      disabled={disabled}
      title={disabled ? 'Reordering unavailable' : 'Drag to reorder'}
      className={`shrink-0 p-1 rounded transition-colors ${
        disabled
          ? 'text-white/10 cursor-not-allowed'
          : 'text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing touch-none'
      }`}
    >
      <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
        <circle cx="2.5" cy="2.5" r="1.5" />
        <circle cx="7.5" cy="2.5" r="1.5" />
        <circle cx="2.5" cy="8" r="1.5" />
        <circle cx="7.5" cy="8" r="1.5" />
        <circle cx="2.5" cy="13.5" r="1.5" />
        <circle cx="7.5" cy="13.5" r="1.5" />
      </svg>
    </button>
  );
}

// ── Sortable Table (drag-and-drop reordering, dnd-kit) ──
interface SortableTableProps<T> {
  columns: { key: string; label: string; width?: string }[];
  items: T[];
  getId: (item: T) => string;
  renderRow: (
    item: T,
    index: number,
    drag: { handle: React.ReactNode; isDragging: boolean },
  ) => Record<string, React.ReactNode>;
  onReorder: (newItems: T[]) => void;
  empty?: string;
  /** Disable dragging (e.g. filters are active so the visible subset isn't the full order). */
  disabled?: boolean;
}

function SortableTableRow<T>({
  id,
  item,
  index,
  disabled,
  columns,
  renderRow,
}: {
  id: string;
  item: T;
  index: number;
  disabled?: boolean;
  columns: { key: string; label: string; width?: string }[];
  renderRow: SortableTableProps<T>['renderRow'];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const handle = <DragHandle attributes={attributes} listeners={listeners} disabled={disabled} />;
  const cells = renderRow(item, index, { handle, isDragging });

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`transition-colors ${isDragging ? 'relative z-10 bg-white/[0.06] shadow-lg' : 'hover:bg-white/[0.03]'}`}
    >
      {columns.map((col) => (
        <td key={col.key} className="px-4 py-3 text-white/80">
          {cells[col.key]}
        </td>
      ))}
    </tr>
  );
}

export function SortableTable<T>({ columns, items, getId, renderRow, onReorder, empty, disabled }: SortableTableProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map(getId);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  }

  return (
    <div className="overflow-x-auto rounded-brand-lg bg-white/[0.02] ring-1 ring-inset ring-white/[0.02]">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.02] bg-white/[0.02]">
            {columns.map((col) => (
              <th key={col.key} className={`text-left px-4 py-3 text-xs font-semibold text-white/40 uppercase tracking-wider ${col.width || ''}`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-12 text-white/30 text-sm">
                {empty || 'No data found'}
              </td>
            </tr>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {items.map((item, i) => (
                  <SortableTableRow
                    key={ids[i]}
                    id={ids[i]}
                    item={item}
                    index={i}
                    disabled={disabled}
                    columns={columns}
                    renderRow={renderRow}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Filter Bar ──
type FilterOption = string | { label: string; value: string };

interface FilterBarProps {
  filters: { label: string; options: FilterOption[]; value: string; onChange: (v: string) => void }[];
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
}
export function FilterBar({ filters, search }: FilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {search && (
        <input
          type="text"
          value={search.value}
          onChange={(e) => search.onChange(e.target.value)}
          placeholder={search.placeholder || 'Search…'}
          className="px-3 py-1.5 rounded-brand-md bg-white/5 border border-white/[0.03] text-sm text-white/80 placeholder-white/30 focus:outline-none focus:border-[#C3FB15]/50 focus:ring-1 focus:ring-[#C3FB15]/20 w-48 transition-colors"
        />
      )}
      {filters.map((f) => (
        <select
          key={f.label}
          value={f.value}
          onChange={(e) => f.onChange(e.target.value)}
          className="px-3 py-1.5 rounded-brand-md bg-white/5 border border-white/[0.03] text-sm text-white/80 focus:outline-none focus:border-[#C3FB15]/50 focus:ring-1 focus:ring-[#C3FB15]/20 transition-colors"
        >
          <option value="">{f.label}: All</option>
          {f.options.map((o) => {
            const val = typeof o === 'string' ? o : o.value;
            const lbl = typeof o === 'string' ? o : o.label;
            return <option key={val} value={val}>{lbl}</option>;
          })}
        </select>
      ))}
    </div>
  );
}

// ── Button ──
interface BtnProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md';
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  title?: string;
}
export function Btn({ children, variant = 'secondary', size = 'md', onClick, disabled, className = '', type = 'button', title }: BtnProps) {
  const base = 'inline-flex items-center gap-1.5 font-medium rounded-brand-md transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer';
  const sizes = { sm: 'px-2.5 py-1 text-xs', md: 'px-3.5 py-1.5 text-sm' };
  const variants = {
    primary: 'bg-[#C3FB15] hover:bg-[#A8D912] text-[#0A0A0A] border border-[#C3FB15] hover:shadow-green-glow-sm',
    secondary: 'bg-white/8 hover:bg-white/12 text-white/80 border border-white/[0.03]',
    danger: 'bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30',
    ghost: 'text-white/50 hover:text-white/90 hover:bg-white/5',
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title} className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}>
      {children}
    </button>
  );
}

// ── Section Card ──
export function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-[#111111] border border-white/[0.02] rounded-brand-lg ${className}`}>
      {children}
    </div>
  );
}

// ── Alert Banner ──
export function AlertBanner({ type, message, onClick }: { type: 'warning' | 'error' | 'info'; message: string; onClick?: () => void }) {
  const styles = {
    warning: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
    error: 'bg-red-500/10 border-red-500/30 text-red-400',
    info: 'bg-white/8 border-white/15 text-white/70',
  };
  const icons = { warning: '⚠', error: '✕', info: 'ℹ' };
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-brand-md border text-sm mb-4 ${styles[type]} ${onClick ? 'cursor-pointer hover:brightness-125 transition-[filter]' : ''}`}
    >
      <span>{icons[type]}</span>
      <span>{message}</span>
    </div>
  );
}

// ── Tab Bar ──
interface TabBarProps {
  tabs: string[];
  active: string;
  onChange: (t: string) => void;
}
export function TabBar({ tabs, active, onChange }: TabBarProps) {
  return (
    <div className="flex items-center gap-1 border-b border-white/[0.02] mb-6">
      {tabs.map((t) => (
        <button
          key={t}
          onClick={() => onChange(t)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${
            active === t
              ? 'border-[#C3FB15] text-[#C3FB15]'
              : 'border-transparent text-white/40 hover:text-white/80'
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}
